import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';
import { Type } from '@silvertours/front-shared';

// One entry for each key that Contentful can send. Each icon is a separate
// chunk, so a page loads only the icons that it renders. Do not import the
// `@engineering/icons` or `@engineering/icons/car-types` barrels here: they
// put every icon into the bundle.
//
// Each entry must have `ssr: false`. The client cannot preload an icon chunk
// before hydration: `@engineering/icons` resolves `import()` to `.mjs` but
// `require.resolveWeak` to `.cjs`, so the module ID that Next.js generates for
// the preload is `null`. An icon that the server rendered is thus not loaded
// at hydration, and React throws error #418. Without `ssr: false`, the server
// renders the icon.
//
// `satisfies` makes a missing key a compile error. Thus a key that is added to
// `Type.Icon` or `Type.CarTypeIcon` without an entry here does not show a blank
// icon.

export const icons = {
  Add: dynamic(() => import('@engineering/icons/add'), { ssr: false }),
  AddDriver: dynamic(() => import('@engineering/icons/add-driver'), {
    ssr: false,
  }),
  AddToCalendar: dynamic(() => import('@engineering/icons/add-to-calendar'), {
    ssr: false,
  }),
  AirConditioning: dynamic(
    () => import('@engineering/icons/air-conditioning'),
    { ssr: false },
  ),
  Airport: dynamic(() => import('@engineering/icons/airport'), { ssr: false }),
  Android: dynamic(() => import('@engineering/icons/android'), { ssr: false }),
  ArrowBack: dynamic(() => import('@engineering/icons/arrow-back'), {
    ssr: false,
  }),
  ArrowDown: dynamic(() => import('@engineering/icons/arrow-down'), {
    ssr: false,
  }),
  ArrowForward: dynamic(() => import('@engineering/icons/arrow-forward'), {
    ssr: false,
  }),
  ArrowLast: dynamic(() => import('@engineering/icons/arrow-last'), {
    ssr: false,
  }),
  ArrowNext: dynamic(() => import('@engineering/icons/arrow-next'), {
    ssr: false,
  }),
  ArrowUp: dynamic(() => import('@engineering/icons/arrow-up'), { ssr: false }),
  Automatic: dynamic(() => import('@engineering/icons/automatic'), {
    ssr: false,
  }),
  BabySeat: dynamic(() => import('@engineering/icons/baby-seat'), {
    ssr: false,
  }),
  BmLogoOld: dynamic(() => import('@engineering/icons/bm-logo-old'), {
    ssr: false,
  }),
  Border: dynamic(() => import('@engineering/icons/border'), { ssr: false }),
  Calendar: dynamic(() => import('@engineering/icons/calendar'), {
    ssr: false,
  }),
  Cancel: dynamic(() => import('@engineering/icons/cancel'), { ssr: false }),
  Cancel24H: dynamic(() => import('@engineering/icons/cancel-24h'), {
    ssr: false,
  }),
  CarTypeL: dynamic(() => import('@engineering/icons/car-type-l'), {
    ssr: false,
  }),
  CarTypeM: dynamic(() => import('@engineering/icons/car-type-m'), {
    ssr: false,
  }),
  CarTypeS: dynamic(() => import('@engineering/icons/car-type-s'), {
    ssr: false,
  }),
  Checked: dynamic(() => import('@engineering/icons/checked'), { ssr: false }),
  ChildSeat01: dynamic(() => import('@engineering/icons/child-seat-01'), {
    ssr: false,
  }),
  ChildSeat02: dynamic(() => import('@engineering/icons/child-seat-02'), {
    ssr: false,
  }),
  CityStation: dynamic(() => import('@engineering/icons/city-station'), {
    ssr: false,
  }),
  Close: dynamic(() => import('@engineering/icons/close'), { ssr: false }),
  Comment: dynamic(() => import('@engineering/icons/comment'), { ssr: false }),
  Contact: dynamic(() => import('@engineering/icons/contact'), { ssr: false }),
  Copy: dynamic(() => import('@engineering/icons/copy'), { ssr: false }),
  Damage: dynamic(() => import('@engineering/icons/damage'), { ssr: false }),
  Delete: dynamic(() => import('@engineering/icons/delete'), { ssr: false }),
  Dimensions: dynamic(() => import('@engineering/icons/dimensions'), {
    ssr: false,
  }),
  Document: dynamic(() => import('@engineering/icons/document'), {
    ssr: false,
  }),
  Doors: dynamic(() => import('@engineering/icons/doors'), { ssr: false }),
  Download: dynamic(() => import('@engineering/icons/download'), {
    ssr: false,
  }),
  Edit: dynamic(() => import('@engineering/icons/edit'), { ssr: false }),
  ElectroHybrid: dynamic(() => import('@engineering/icons/electro-hybrid'), {
    ssr: false,
  }),
  Email: dynamic(() => import('@engineering/icons/email'), { ssr: false }),
  Facebook: dynamic(() => import('@engineering/icons/facebook'), {
    ssr: false,
  }),
  Ferry: dynamic(() => import('@engineering/icons/ferry'), { ssr: false }),
  Filter: dynamic(() => import('@engineering/icons/filter'), { ssr: false }),
  FloytCut: dynamic(() => import('@engineering/icons/floyt-cut'), {
    ssr: false,
  }),
  FloytFull: dynamic(() => import('@engineering/icons/floyt-full'), {
    ssr: false,
  }),
  FourWheelDrive: dynamic(() => import('@engineering/icons/four-wheel-drive'), {
    ssr: false,
  }),
  FrontWheelDrive: dynamic(
    () => import('@engineering/icons/front-wheel-drive'),
    { ssr: false },
  ),
  Fuel: dynamic(() => import('@engineering/icons/fuel'), { ssr: false }),
  FuelLevel: dynamic(() => import('@engineering/icons/fuel-level'), {
    ssr: false,
  }),
  Gps: dynamic(() => import('@engineering/icons/gps'), { ssr: false }),
  Heart: dynamic(() => import('@engineering/icons/heart'), { ssr: false }),
  Help: dynamic(() => import('@engineering/icons/help'), { ssr: false }),
  Hide: dynamic(() => import('@engineering/icons/hide'), { ssr: false }),
  Hotel: dynamic(() => import('@engineering/icons/hotel'), { ssr: false }),
  Info: dynamic(() => import('@engineering/icons/info'), { ssr: false }),
  Instagram: dynamic(() => import('@engineering/icons/instagram'), {
    ssr: false,
  }),
  Ios: dynamic(() => import('@engineering/icons/ios'), { ssr: false }),
  Like: dynamic(() => import('@engineering/icons/like'), { ssr: false }),
  LocateMe: dynamic(() => import('@engineering/icons/locate-me'), {
    ssr: false,
  }),
  Location: dynamic(() => import('@engineering/icons/location'), {
    ssr: false,
  }),
  Locked: dynamic(() => import('@engineering/icons/locked'), { ssr: false }),
  MapView: dynamic(() => import('@engineering/icons/map-view'), { ssr: false }),
  Menu: dynamic(() => import('@engineering/icons/menu'), { ssr: false }),
  Messenger: dynamic(() => import('@engineering/icons/messenger'), {
    ssr: false,
  }),
  Money: dynamic(() => import('@engineering/icons/money'), { ssr: false }),
  Next: dynamic(() => import('@engineering/icons/next'), { ssr: false }),
  NoInternet: dynamic(() => import('@engineering/icons/no-internet'), {
    ssr: false,
  }),
  Pause: dynamic(() => import('@engineering/icons/pause'), { ssr: false }),
  Phone: dynamic(() => import('@engineering/icons/phone'), { ssr: false }),
  PickupStation: dynamic(() => import('@engineering/icons/pickup-station'), {
    ssr: false,
  }),
  Pinterest: dynamic(() => import('@engineering/icons/pinterest'), {
    ssr: false,
  }),
  Play: dynamic(() => import('@engineering/icons/play'), { ssr: false }),
  RearWheelDrive: dynamic(() => import('@engineering/icons/rear-wheel-drive'), {
    ssr: false,
  }),
  Reload: dynamic(() => import('@engineering/icons/reload'), { ssr: false }),
  Requested: dynamic(() => import('@engineering/icons/requested'), {
    ssr: false,
  }),
  RoofBox: dynamic(() => import('@engineering/icons/roof-box'), { ssr: false }),
  RoofRack: dynamic(() => import('@engineering/icons/roof-rack'), {
    ssr: false,
  }),
  Search: dynamic(() => import('@engineering/icons/search'), { ssr: false }),
  SeatExtension: dynamic(() => import('@engineering/icons/seat-extension'), {
    ssr: false,
  }),
  Seats: dynamic(() => import('@engineering/icons/seats'), { ssr: false }),
  ShareAndroid: dynamic(() => import('@engineering/icons/share-android'), {
    ssr: false,
  }),
  ShareIos: dynamic(() => import('@engineering/icons/share-ios'), {
    ssr: false,
  }),
  Show: dynamic(() => import('@engineering/icons/show'), { ssr: false }),
  ShuttleBus: dynamic(() => import('@engineering/icons/shuttle-bus'), {
    ssr: false,
  }),
  SkiCarrier: dynamic(() => import('@engineering/icons/ski-carrier'), {
    ssr: false,
  }),
  Sms: dynamic(() => import('@engineering/icons/sms'), { ssr: false }),
  SnowChains: dynamic(() => import('@engineering/icons/snow-chains'), {
    ssr: false,
  }),
  Sorting: dynamic(() => import('@engineering/icons/sorting'), { ssr: false }),
  StarEmpty: dynamic(() => import('@engineering/icons/star-empty'), {
    ssr: false,
  }),
  StarFull: dynamic(() => import('@engineering/icons/star-full'), {
    ssr: false,
  }),
  StarHalf: dynamic(() => import('@engineering/icons/star-half'), {
    ssr: false,
  }),
  StationMarker: dynamic(() => import('@engineering/icons/station-marker'), {
    ssr: false,
  }),
  Substract: dynamic(() => import('@engineering/icons/substract'), {
    ssr: false,
  }),
  Terminal: dynamic(() => import('@engineering/icons/terminal'), {
    ssr: false,
  }),
  Tipp: dynamic(() => import('@engineering/icons/tipp'), { ssr: false }),
  TrainStation: dynamic(() => import('@engineering/icons/train-station'), {
    ssr: false,
  }),
  Transmission: dynamic(() => import('@engineering/icons/transmission'), {
    ssr: false,
  }),
  Unlocked: dynamic(() => import('@engineering/icons/unlocked'), {
    ssr: false,
  }),
  Upload: dynamic(() => import('@engineering/icons/upload'), { ssr: false }),
  User: dynamic(() => import('@engineering/icons/user'), { ssr: false }),
  Warning: dynamic(() => import('@engineering/icons/warning'), { ssr: false }),
  Weight: dynamic(() => import('@engineering/icons/weight'), { ssr: false }),
  Whatsapp: dynamic(() => import('@engineering/icons/whatsapp'), {
    ssr: false,
  }),
  Wifi: dynamic(() => import('@engineering/icons/wifi'), { ssr: false }),
  WinterTires: dynamic(() => import('@engineering/icons/winter-tires'), {
    ssr: false,
  }),
} satisfies Record<Type.Icon, ComponentType>;

export const carTypeIcons = {
  Compact: dynamic(() => import('@engineering/icons/car-types/compact'), {
    ssr: false,
  }),
  Convertible: dynamic(
    () => import('@engineering/icons/car-types/convertible'),
    { ssr: false },
  ),
  Medium: dynamic(() => import('@engineering/icons/car-types/medium'), {
    ssr: false,
  }),
  Premium: dynamic(() => import('@engineering/icons/car-types/premium'), {
    ssr: false,
  }),
  Small: dynamic(() => import('@engineering/icons/car-types/small'), {
    ssr: false,
  }),
  Sport: dynamic(() => import('@engineering/icons/car-types/sport'), {
    ssr: false,
  }),
  Station: dynamic(() => import('@engineering/icons/car-types/station'), {
    ssr: false,
  }),
  Suv: dynamic(() => import('@engineering/icons/car-types/suv'), {
    ssr: false,
  }),
  Transporter: dynamic(
    () => import('@engineering/icons/car-types/transporter'),
    { ssr: false },
  ),
  Upper: dynamic(() => import('@engineering/icons/car-types/upper'), {
    ssr: false,
  }),
  Van: dynamic(() => import('@engineering/icons/car-types/van'), {
    ssr: false,
  }),
} satisfies Record<Type.CarTypeIcon, ComponentType>;
